import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/constants";
import { FileUpload } from "@/components/FileUpload";
import { RichEditor } from "@/components/RichEditor";
import { Zap, FileText, Radar, Target, Trash2, Gauge, Users, Inbox, CalendarDays, Pencil, UserPlus, Gift, Copy, BookOpen, UserCircle, Star, Ticket, ChevronDown, Eye, Archive, Search, Save, Clock, Send, Plus } from "lucide-react";
import { MemberIntel, AuditLogs } from "@/components/admin/MemberIntel";
import { BillingCenter } from "@/components/billing/BillingCenter";
import { ConvictionStudio, FeatureToggle } from "@/components/conviction/ConvictionStudio";
import { useFeature } from "@/components/conviction/shared";
import { Treasury, AnalystAnalytics } from "@/components/billing/Treasury";
import { CreditCard, Landmark, BarChart3 } from "lucide-react";
import { CONVICTION_STATUSES, convictionBadgeClass } from "@/lib/conviction";

const CATS = [
  ["alpha_feed", "Alpha Feed"], ["research", "Research"], ["conviction", "Conviction"],
  ["smart_money", "Smart Money"], ["calendar", "Alpha Calendar"], ["sea", "SEA Desk"], ["indonesia", "Indonesia Desk"],
];
const CAT_KEYS = CATS.map((c) => c[0]);

const inputCls = "w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-2.5 text-sm focus:border-[#ccff00]/50 focus:outline-none";
const labelCls = "block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5 mt-4";
const Field = ({ label, children }) => (<div><label className={labelCls}>{label}</label>{children}</div>);
const btn = "px-6 py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter]";

function ExclusiveField({ value, onChange, testid = "exclusive-toggle" }) {
  return (
    <div className="flex items-center gap-3 my-3 p-3 rounded-xl border border-white/10">
      <button type="button" data-testid={testid} onClick={() => onChange(!value)} className={`shrink-0 w-10 h-6 rounded-full relative transition-colors ${value ? "bg-[#ccff00]" : "bg-zinc-700"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all ${value ? "left-[18px]" : "left-0.5"}`} />
      </button>
      <div>
        <p className="text-sm font-medium">{value ? "Exclusive content" : "Free content"}</p>
        <p className="text-xs text-zinc-500">{value ? "Only members with access (whitelist / code / subscription) can open this" : "Anyone can read this"}</p>
      </div>
    </div>
  );
}

function AnalystPicker({ analysts, value, onChange }) {
  if (!analysts || analysts.length === 0) return null;
  return (
    <Field label="Attribute to Analyst (optional)">
      <select data-testid="analyst-picker" className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">OnchainLyfe Desk (house)</option>
        {analysts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </Field>
  );
}

function FinanceRedirect({ to, setTab }) {
  useEffect(() => { setTab(to); }, [to, setTab]);
  return null;
}

export default function Admin() {
  const { user, loading, isAdmin, isAnalyst, isStaff, isEditor, logout } = useAuth();
  const query = new URLSearchParams(useLocation().search);
  const mapLegacy = (t) => ({ overview: "dashboard", quick: "create", article: "create", manage: "content", profile: "settings", users: "members", whitelist: "members", codes: "members", referrals: "members", requests: "applications" }[t] || t);
  const [tab, setTab] = useState(mapLegacy(query.get("tab") || "dashboard"));
  const [stats, setStats] = useState(null);
  const [edit, setEdit] = useState(null); // {kind, doc}
  const [conviction, setConviction] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [perms, setPerms] = useState([]);
  const convV2 = useFeature("conviction_v2");
  useEffect(() => { if (user) client.get("/billing/me/permissions").then((r) => setPerms(r.data.permissions)).catch(() => {}); }, [user]);

  const refresh = () => {
    client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    client.get("/conviction").then((r) => setConviction(r.data)).catch(() => {});
    client.get("/analysts").then((r) => setAnalysts(r.data)).catch(() => {});
  };
  useEffect(() => { if (isEditor) refresh(); }, [user]);

  if (loading) return <div className="py-24 text-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const financeOnly = !isEditor && (user.finance_roles || []).length > 0;
  if (!isEditor && !financeOnly) return <Navigate to="/" replace />;

  const openEdit = (kind, doc) => { setEdit({ kind, doc }); setTab(kind === "conviction" ? "conviction" : "create"); };
  const afterSave = (msg) => { setEdit(null); refresh(); toast.success(msg); };
  const pickerAnalysts = isStaff ? analysts : [];

  const NAV = financeOnly ? [
    ...(perms.includes("billing.view") ? [["billing", "Billing", CreditCard]] : []),
    ...(perms.includes("treasury.view") ? [["treasury", "Treasury", Landmark]] : []),
  ] : [
    ["dashboard", "Dashboard", Gauge],
    ["create", edit ? "Edit Post" : "Create Post", FileText],
    ["content", "Content", Pencil],
    ["conviction", "Conviction", Target],
    ...(isAnalyst ? [["modules", "Modules", BookOpen]] : []),
    ...(isStaff ? [["calendar", "Calendar", CalendarDays]] : []),
    ...(isAdmin ? [["analysts", "Analysts", Star]] : []),
    ...((isAdmin || isAnalyst) ? [["members", "Members", Users]] : []),
    ...(isAdmin ? [["applications", "Applications", Inbox]] : []),
    ...(perms.includes("billing.view") ? [["billing", "Billing", CreditCard]] : []),
    ...(perms.includes("treasury.view") ? [["treasury", "Treasury", Landmark]] : []),
    ...(isAnalyst && !isAdmin ? [["analytics", "Subscriber Analytics", BarChart3]] : []),
    ["settings", "Settings", UserCircle],
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <a href="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-[#ccff00] transition-colors" data-testid="admin-view-site">
          <img src={LOGO_URL} alt="OnchainLyfe" className="h-6 w-auto" /> <span className="hidden sm:inline">View site</span>
        </a>
        <button data-testid="admin-logout" onClick={() => { logout(); }} className="text-sm text-zinc-400 hover:text-white transition-colors">Log out</button>
      </div>
      <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-1">Editorial Console</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-6">{isAdmin ? "OnchainLyfe CMS" : isAnalyst ? "Analyst Studio" : financeOnly ? "Finance Console" : "Publisher Desk"}</h1>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
        {/* Sidebar (desktop) / horizontal scroll (mobile) */}
        <aside className="mb-6 lg:mb-0">
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {NAV.map(([k, label, Icon]) => (
              <button key={k} data-testid={`admin-nav-${k}`} onClick={() => { if (k !== "create" && k !== "conviction") setEdit(null); setTab(k); }}
                className={`shrink-0 flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap lg:w-full ${tab === k ? "bg-[#ccff00] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">
          {financeOnly && !NAV.some(([k]) => k === tab) && NAV[0] && <FinanceRedirect to={NAV[0][0]} setTab={setTab} />}
          {tab === "dashboard" && !financeOnly && <Overview stats={stats} />}
          {tab === "create" && <CreatePost initial={edit?.kind === "article" ? edit.doc : null} analysts={pickerAnalysts} isAnalyst={isAnalyst} onDone={() => afterSave("Post saved.")} />}
          {tab === "content" && <ContentManager onEdit={(doc) => openEdit("article", doc)} />}
          {tab === "conviction" && isAdmin && <FeatureToggle />}
          {tab === "conviction" && convV2 && <ConvictionStudio analysts={analysts} />}
          {tab === "conviction" && !convV2 && <ConvictionSection analysts={pickerAnalysts} onChange={refresh} />}
          {tab === "modules" && isAnalyst && <ModulesManager isAnalyst={isAnalyst} analysts={pickerAnalysts} />}
          {tab === "calendar" && isStaff && <CalendarManager onChange={refresh} />}
          {tab === "analysts" && isAdmin && <AnalystsManager />}
          {tab === "members" && <MembersManager isAdmin={isAdmin} isAnalyst={isAnalyst} analysts={analysts} />}
          {tab === "applications" && isAdmin && <RequestsManager />}
          {tab === "billing" && perms.includes("billing.view") && <BillingCenter />}
          {tab === "treasury" && perms.includes("treasury.view") && <Treasury perms={perms} />}
          {tab === "analytics" && isAnalyst && <AnalystAnalytics />}
          {tab === "settings" && (isAnalyst ? <MyProfile /> : <SettingsPanel />)}
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  return (
    <div className="cl-card p-8" style={{ transform: "none" }} data-testid="settings-panel">
      <h3 className="font-display text-xl font-bold mb-2">Settings</h3>
      <p className="text-sm text-zinc-400">Account and publication settings. Members, access codes and applications are managed from their dedicated sections in the sidebar.</p>
    </div>
  );
}

function MembersManager({ isAdmin, isAnalyst, analysts }) {
  const sub = [
    ...(isAdmin ? [["members", "Member Intelligence"], ["users", "Users"]] : []),
    ["whitelist", "Whitelist"],
    ["codes", "Access Codes"],
    ...(isAdmin ? [["referrals", "Referrals"], ["audit", "Audit & Recovery"]] : []),
  ];
  const [s, setS] = useState(sub[0][0]);
  return (
    <div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {sub.map(([k, l]) => (
          <button key={k} data-testid={`members-sub-${k}`} onClick={() => setS(k)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${s === k ? "bg-[#ccff00] text-black" : "border border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      {s === "members" && isAdmin && <MemberIntel />}
      {s === "users" && isAdmin && <UsersManager analysts={analysts} />}
      {s === "audit" && isAdmin && <AuditLogs />}
      {s === "whitelist" && <WhitelistManager />}
      {s === "codes" && <CodesManager isAnalyst={isAnalyst} analysts={isAnalyst ? [] : analysts} />}
      {s === "referrals" && isAdmin && <ReferralsManager />}
    </div>
  );
}

function CreatePost({ initial, analysts, isAnalyst, onDone }) {
  const empty = {
    type: isAnalyst ? "research" : "news", title: "", summary: "", hero_image: "", body: "",
    analyst_id: "", category: "Markets", source_name: "", source_url: "", tier: "public",
    flag: "standard", featured: false, seo_title: "", seo_description: "", slug: "",
    id_title: "", id_body: "", scheduled_at: "",
  };
  const [f, setF] = useState(empty);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [docId, setDocId] = useState(null);
  const set = (k) => (e) => { setF((p) => ({ ...p, [k]: e.target.value })); setDirty(true); };

  useEffect(() => {
    const load = (d) => setF({
      ...empty, type: d.type || "news", title: d.title || "", summary: d.summary || "",
      hero_image: d.hero_image || "", body: d.body || "", analyst_id: d.analyst_id || "",
      category: d.category || "Markets", source_name: d.source_name || "", source_url: d.source_url || "",
      tier: d.tier === "public" ? "public" : "member", flag: d.flag || (d.is_breaking ? "breaking" : "standard"),
      featured: !!d.featured, seo_title: d.seo_title || "", seo_description: d.seo_description || "",
      slug: d.slug || "", id_title: d.translations?.id?.title || "", id_body: d.translations?.id?.body || "",
      scheduled_at: d.scheduled_at ? d.scheduled_at.slice(0, 16) : "",
    });
    if (initial?.id) {
      setDocId(initial.id);
      // fetch full doc (staff can view any status)
      if (initial.slug && initial.body === undefined) {
        client.get(`/articles/${initial.slug}`).then((r) => load(r.data)).catch(() => load(initial));
      } else load(initial);
    } else { setF(empty); setDocId(null); }
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const buildPayload = (status) => ({
    title: f.title, type: f.type, summary: f.summary, hero_image: f.hero_image, body: f.body,
    category: f.category, source_name: f.source_name, source_url: f.source_url,
    tier: f.tier, flag: f.flag, featured: f.featured, analyst_id: f.analyst_id || "",
    seo_title: f.seo_title, seo_description: f.seo_description, slug: f.slug || null,
    status, scheduled_at: status === "scheduled" && f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
    translations: (f.id_title || f.id_body) ? { id: { title: f.id_title, summary: f.summary, body: f.id_body } } : {},
  });

  const save = async (status, { preview } = {}) => {
    if (!f.title.trim()) { toast.error("Title is required."); return; }
    if (status === "scheduled" && !f.scheduled_at) { toast.error("Pick a publish date to schedule."); return; }
    setBusy(true);
    try {
      const payload = buildPayload(status);
      const res = docId ? await client.put(`/articles/${docId}`, payload) : await client.post("/articles", payload);
      setDirty(false);
      if (preview) { window.open(`/article/${res.data.slug}`, "_blank"); setBusy(false); return; }
      toast.success(status === "draft" ? "Draft saved." : status === "scheduled" ? "Post scheduled." : "Post published.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="create-post">
      <div className="flex gap-2 mb-5">
        {[["news", "News"], ["research", "Research"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`post-type-${k}`} onClick={() => { setF({ ...f, type: k }); setDirty(true); }}
            className={`px-5 py-2 rounded-full text-sm font-semibold ${f.type === k ? "bg-[#ccff00] text-black" : "border border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
      </div>

      <Field label="Title"><input data-testid="post-title" className={inputCls} value={f.title} onChange={set("title")} placeholder="Post headline" /></Field>
      <Field label="Short summary"><textarea data-testid="post-summary" className={inputCls} rows={2} value={f.summary} onChange={set("summary")} placeholder="One or two sentence excerpt" /></Field>

      <label className={labelCls}>Cover image (16:9)</label>
      <FileUpload value={f.hero_image} onChange={(v) => { setF((p) => ({ ...p, hero_image: v })); setDirty(true); }} aspect="16/9" previewClass="w-full max-w-md" testid="post-cover" />

      <label className={labelCls}>Content</label>
      <RichEditor value={f.body} onChange={(html) => { setF((p) => ({ ...p, body: html })); setDirty(true); }} />

      {/* Post Settings (collapsible) */}
      <div className="mt-6 rounded-xl border border-white/10">
        <button type="button" data-testid="post-settings-toggle" onClick={() => setSettingsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold">
          Post Settings <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
        </button>
        {settingsOpen && (
          <div className="px-4 pb-5 border-t border-white/10" data-testid="post-settings">
            {analysts && analysts.length > 0 && <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => { setF({ ...f, analyst_id: v }); setDirty(true); }} />}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Category"><input data-testid="post-category" className={inputCls} value={f.category} onChange={set("category")} /></Field>
              <Field label="Access"><select data-testid="post-access" className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="member">Members</option></select></Field>
              <Field label="Source name"><input className={inputCls} value={f.source_name} onChange={set("source_name")} /></Field>
              <Field label="Source URL"><input className={inputCls} value={f.source_url} onChange={set("source_url")} /></Field>
              <Field label="Status flag"><select data-testid="post-flag" className={inputCls} value={f.flag} onChange={set("flag")}><option value="standard">Standard</option><option value="breaking">Breaking</option><option value="developing">Developing</option></select></Field>
              <Field label="Featured"><select className={inputCls} value={f.featured ? "1" : "0"} onChange={(e) => { setF({ ...f, featured: e.target.value === "1" }); setDirty(true); }}><option value="0">No</option><option value="1">Yes</option></select></Field>
              <Field label="Slug (optional)"><input data-testid="post-slug" className={inputCls} value={f.slug} onChange={set("slug")} placeholder="auto from title" /></Field>
              <Field label="Schedule (publish date)"><input data-testid="post-schedule" type="datetime-local" className={inputCls} value={f.scheduled_at} onChange={set("scheduled_at")} /></Field>
              <Field label="SEO title"><input className={inputCls} value={f.seo_title} onChange={set("seo_title")} /></Field>
              <Field label="SEO description"><input className={inputCls} value={f.seo_description} onChange={set("seo_description")} /></Field>
            </div>
            <Field label="Indonesian title (optional)"><input className={inputCls} value={f.id_title} onChange={set("id_title")} /></Field>
            <Field label="Indonesian body (optional)"><textarea className={inputCls} rows={3} value={f.id_body} onChange={set("id_body")} /></Field>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mt-6 sticky bottom-0 py-3">
        <button type="button" data-testid="post-save-draft" disabled={busy} onClick={() => save("draft")} className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"><Save className="w-4 h-4" /> Save Draft</button>
        <button type="button" data-testid="post-preview" disabled={busy} onClick={() => save("draft", { preview: true })} className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"><Eye className="w-4 h-4" /> Preview</button>
        <button type="button" data-testid="post-schedule-btn" disabled={busy} onClick={() => save("scheduled")} className="px-5 py-2.5 rounded-full border border-[#ccff00]/40 text-[#ccff00] text-sm font-semibold flex items-center gap-2 disabled:opacity-50"><Clock className="w-4 h-4" /> Schedule</button>
        <button type="button" data-testid="post-publish" disabled={busy} onClick={() => save("published")} className={`${btn} flex items-center gap-2 disabled:opacity-50`}><Send className="w-4 h-4" /> {busy ? "Saving…" : "Publish"}</button>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  published: "text-[#ccff00]", draft: "text-zinc-400", scheduled: "text-amber-400", archived: "text-zinc-600",
};

function ContentManager({ onEdit }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [access, setAccess] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (type) p.set("type", type);
    if (status) p.set("status", status);
    if (access) p.set("access", access);
    client.get(`/admin/content?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, type, status, access]);

  const setStatusOf = async (row, st) => {
    setBusyId(row.id);
    try {
      await client.patch(`/articles/${row.id}/status`, { status: st });
      toast.success(st === "archived" ? "Archived." : "Restored to published.");
      load();
    } catch { toast.error("Action failed."); }
    finally { setBusyId(null); }
  };
  const hardDelete = async (row) => {
    if (row.status !== "draft") { toast.error("Only drafts can be permanently deleted. Archive instead."); return; }
    if (!window.confirm(`Permanently delete draft "${row.title}"?`)) return;
    setBusyId(row.id);
    try { await client.delete(`/articles/${row.id}`); toast.success("Draft deleted."); load(); }
    catch { toast.error("Delete failed."); }
    finally { setBusyId(null); }
  };

  const Sel = ({ value, onChange, opts, testid }) => (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-lg bg-[#0d0d0d] border border-white/10 px-3 py-2 text-xs">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div data-testid="content-manager">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title…" className="rounded-lg bg-[#0d0d0d] border border-white/10 pl-9 pr-3 py-2 text-sm w-56" />
        </div>
        <Sel testid="content-filter-type" value={type} onChange={setType} opts={[["", "All types"], ["news", "News"], ["research", "Research"]]} />
        <Sel testid="content-filter-status" value={status} onChange={setStatus} opts={[["", "All status"], ["published", "Published"], ["draft", "Draft"], ["scheduled", "Scheduled"], ["archived", "Archived / Legacy"]]} />
        <Sel testid="content-filter-access" value={access} onChange={setAccess} opts={[["", "All access"], ["public", "Public"], ["members", "Members"]]} />
      </div>
      <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
        <div className="hidden sm:grid grid-cols-[1fr_90px_90px_80px_110px_150px] gap-3 px-4 py-2.5 border-b border-white/10 text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500">
          <span>Title</span><span>Type</span><span>Status</span><span>Access</span><span>Updated</span><span className="text-right">Actions</span>
        </div>
        {rows.length === 0 ? <p className="text-zinc-500 p-8">No content matches.</p> : rows.map((r) => (
          <div key={r.id} data-testid={`content-row-${r.id}`} className="grid sm:grid-cols-[1fr_90px_90px_80px_110px_150px] gap-3 px-4 py-3 border-b border-white/5 items-center">
            <div className="min-w-0"><p className="text-sm font-medium truncate">{r.title}</p><p className="font-mono-cl text-[10px] uppercase text-zinc-500 truncate">{r.author} · {r.category}</p></div>
            <span className="text-xs text-zinc-400 uppercase">{r.type}</span>
            <span className={`text-xs font-mono-cl uppercase ${STATUS_STYLE[r.status] || "text-zinc-400"}`}>{r.status}</span>
            <span className="text-xs text-zinc-400">{r.tier === "member" ? "Members" : "Public"}</span>
            <span className="text-[11px] text-zinc-500">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "-"}</span>
            <div className="flex gap-1 justify-start sm:justify-end">
              <button data-testid={`content-edit-${r.id}`} title="Edit" onClick={() => onEdit(r)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
              <a data-testid={`content-preview-${r.id}`} title="Preview" href={`/article/${r.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Eye className="w-4 h-4" /></a>
              {r.status === "archived" ? (
                <button data-testid={`content-restore-${r.id}`} title="Restore" disabled={busyId === r.id} onClick={() => setStatusOf(r, "published")} className="p-2 rounded-lg text-emerald-400 hover:bg-emerald-500/10"><Send className="w-4 h-4" /></button>
              ) : (
                <button data-testid={`content-archive-${r.id}`} title="Archive" disabled={busyId === r.id} onClick={() => setStatusOf(r, "archived")} className="p-2 rounded-lg text-amber-400 hover:bg-amber-500/10"><Archive className="w-4 h-4" /></button>
              )}
              {r.status === "draft" && <button data-testid={`content-delete-${r.id}`} title="Delete draft" disabled={busyId === r.id} onClick={() => hardDelete(r)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <p className="text-zinc-500">Loading stats…</p>;
  const cards = [
    ["Total Articles", stats.articles], ["News", stats.news], ["Research", stats.research],
    ["Alpha Signals", stats.alpha], ["Conviction", stats.conviction], ["Calendar", stats.calendar],
    ["Analysts", stats.analysts], ["Modules", stats.modules],
    ["Users", stats.users], ["Waitlist", stats.waitlist],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map(([label, val]) => (
        <div key={label} className="cl-card p-6" style={{ transform: "none" }}>
          <p className="font-mono-cl text-[10px] tracking-[0.16em] uppercase text-zinc-500">{label}</p>
          <p className="font-display text-4xl font-black text-[#ccff00] mt-2">{val}</p>
        </div>
      ))}
    </div>
  );
}

function QuickPublish({ onDone }) {
  const [f, setF] = useState({ title: "", category: "Markets", summary: "", source_name: "", region: "global", tier: "public", is_breaking: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await client.post("/articles/quick", f); setF({ ...f, title: "", summary: "", source_name: "" }); onDone(); } catch { toast.error("Failed."); } };
  return (
    <form onSubmit={submit} className="max-w-2xl" data-testid="quick-publish-form">
      <div className="rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/[0.03] p-4 mb-6 flex items-center gap-3">
        <Zap className="w-5 h-5 text-[#ccff00]" />
        <p className="text-sm text-zinc-300">Speed is the brand. Publish a breaking update in seconds, then expand into a full article later.</p>
      </div>
      <Field label="Headline"><input data-testid="quick-title" className={inputCls} value={f.title} onChange={set("title")} required /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Category"><input className={inputCls} value={f.category} onChange={set("category")} /></Field>
        <Field label="Source"><input className={inputCls} value={f.source_name} onChange={set("source_name")} /></Field>
      </div>
      <Field label="Short Summary"><textarea className={inputCls} rows={3} value={f.summary} onChange={set("summary")} /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Region"><select className={inputCls} value={f.region} onChange={set("region")}><option value="global">Global</option><option value="sea">SEA</option><option value="indonesia">Indonesia</option></select></Field>
        <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="member">Member</option><option value="alpha">Alpha</option></select></Field>
        <Field label="Breaking?"><select className={inputCls} value={f.is_breaking ? "1" : "0"} onChange={(e) => setF({ ...f, is_breaking: e.target.value === "1" })}><option value="1">Breaking</option><option value="0">Developing</option></select></Field>
      </div>
      <button data-testid="quick-submit" className={`mt-6 ${btn}`}>Publish Breaking Update</button>
    </form>
  );
}

function ArticleForm({ initial, onDone, analysts }) {
  const empty = {
    title: "", type: "news", category: "Markets", region: "global", tier: "public",
    summary: "", hero_image: "", source_name: "", source_url: "", source_badge: "", badges: [],
    signal_score: "", so_what: "", watch_next: "", what_happened: "", why_it_matters: "",
    market_impact: "", data_points: "", cryptolyfe_view: "", what_watching: "", body: "",
    is_breaking: false, featured: false, matters_rank: "", analyst_id: "", exclusive: false,
    tr_id_title: "", tr_id_summary: "", tr_id_so_what: "", tr_id_body: "",
  };
  const fromDoc = (d) => ({
    ...empty, ...d,
    badges: d.badges || [],
    watch_next: (d.watch_next || []).join(", "),
    signal_score: d.signal_score ?? "",
    matters_rank: d.matters_rank ?? "",
    tr_id_title: d.translations?.id?.title || "",
    tr_id_summary: d.translations?.id?.summary || "",
    tr_id_so_what: d.translations?.id?.so_what || "",
    tr_id_body: d.translations?.id?.body || "",
  });
  const [f, setF] = useState(initial ? fromDoc(initial) : empty);
  useEffect(() => { setF(initial ? fromDoc(initial) : empty); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const BADGES = ["BREAKING", "DEVELOPING", "CONFIRMED", "ON-CHAIN", "EXCLUSIVE", "ONCHAINLYFE INTELLIGENCE"];
  const toggleBadge = (b) => { const cur = f.badges || []; setF({ ...f, badges: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] }); };

  const submit = async (e) => {
    e.preventDefault();
    const translations = {};
    if (f.tr_id_title || f.tr_id_summary || f.tr_id_so_what || f.tr_id_body) {
      translations.id = { title: f.tr_id_title, summary: f.tr_id_summary, so_what: f.tr_id_so_what, body: f.tr_id_body };
    }
    const payload = {
      title: f.title, type: f.type, category: f.category, region: f.region, tier: f.tier,
      summary: f.summary, hero_image: f.hero_image, source_name: f.source_name, source_url: f.source_url,
      source_badge: f.source_badge, badges: f.badges,
      signal_score: f.signal_score === "" ? null : parseFloat(f.signal_score),
      so_what: f.so_what, watch_next: f.watch_next ? String(f.watch_next).split(",").map((s) => s.trim()).filter(Boolean) : [],
      what_happened: f.what_happened, why_it_matters: f.why_it_matters, market_impact: f.market_impact,
      data_points: f.data_points, cryptolyfe_view: f.cryptolyfe_view, what_watching: f.what_watching, body: f.body,
      is_breaking: !!f.is_breaking, featured: !!f.featured,
      matters_rank: f.matters_rank === "" ? null : parseInt(f.matters_rank), translations,
      analyst_id: f.analyst_id || "", exclusive: !!f.exclusive,
    };
    try {
      if (initial?.id) await client.put(`/articles/${initial.id}`, payload);
      else await client.post("/articles", payload);
      onDone();
    } catch { toast.error("Failed to save article."); }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl" data-testid="article-form">
      <Field label="Title"><input data-testid="article-title" className={inputCls} value={f.title} onChange={set("title")} required /></Field>
      <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => setF({ ...f, analyst_id: v })} />
      <ExclusiveField value={f.exclusive} onChange={(v) => setF({ ...f, exclusive: v })} testid="article-exclusive" />
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Type"><select className={inputCls} value={f.type} onChange={set("type")}><option value="news">News</option><option value="research">Research</option></select></Field>
        <Field label="Category"><input className={inputCls} value={f.category} onChange={set("category")} /></Field>
        <Field label="Region"><select className={inputCls} value={f.region} onChange={set("region")}><option value="global">Global</option><option value="sea">SEA</option><option value="indonesia">Indonesia</option></select></Field>
        <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="member">Member</option><option value="alpha">Alpha</option></select></Field>
      </div>
      <Field label="Hero Image"><div className="flex flex-col gap-2"><FileUpload value={f.hero_image} onChange={(v) => setF({ ...f, hero_image: v })} label="Upload hero image" testid="article-hero-upload" /><input className={inputCls} value={f.hero_image} onChange={set("hero_image")} placeholder="…or paste an image URL" /></div></Field>
      <Field label="Summary"><textarea className={inputCls} rows={2} value={f.summary} onChange={set("summary")} /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Signal Score (0-10)"><input className={inputCls} type="number" step="0.1" value={f.signal_score} onChange={set("signal_score")} /></Field>
        <Field label="Source Name"><input className={inputCls} value={f.source_name} onChange={set("source_name")} /></Field>
        <Field label="Source Badge"><select className={inputCls} value={f.source_badge} onChange={set("source_badge")}><option value="">None</option><option>PRIMARY SOURCE</option><option>OFFICIAL</option><option>ON-CHAIN VERIFIED</option><option>REGULATORY FILING</option><option>ONCHAINLYFE EXCLUSIVE</option><option>UNCONFIRMED</option></select></Field>
      </div>
      <label className={labelCls}>Badges</label>
      <div className="flex flex-wrap gap-2">
        {BADGES.map((b) => <button type="button" key={b} onClick={() => toggleBadge(b)} className={`px-3 py-1 rounded-full text-[10px] font-mono-cl uppercase border ${(f.badges || []).includes(b) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{b}</button>)}
      </div>
      <div className="rounded-xl border border-[#ccff00]/20 p-4 mt-6">
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-1">"So What?" + Watch Next</p>
        <Field label="So What"><textarea className={inputCls} rows={2} value={f.so_what} onChange={set("so_what")} /></Field>
        <Field label="Watch Next (comma separated)"><input className={inputCls} value={f.watch_next} onChange={set("watch_next")} /></Field>
      </div>
      <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400 mt-6 mb-1">Information Hierarchy (EN)</p>
      {[["what_happened", "What Happened"], ["why_it_matters", "Why It Matters"], ["market_impact", "Market Impact"], ["data_points", "The Data"], ["cryptolyfe_view", "OnchainLyfe View"], ["what_watching", "What We're Watching"]].map(([k, l]) => (
        <Field key={k} label={l}><textarea className={inputCls} rows={2} value={f[k]} onChange={set(k)} /></Field>
      ))}
      <Field label="Full Body (EN)"><textarea className={inputCls} rows={5} value={f.body} onChange={set("body")} /></Field>

      <div className="rounded-xl border border-white/10 p-4 mt-6">
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-1">Indonesian Version (optional)</p>
        <Field label="Judul (ID)"><input data-testid="article-tr-title" className={inputCls} value={f.tr_id_title} onChange={set("tr_id_title")} /></Field>
        <Field label="Ringkasan (ID)"><textarea className={inputCls} rows={2} value={f.tr_id_summary} onChange={set("tr_id_summary")} /></Field>
        <Field label="So What (ID)"><textarea className={inputCls} rows={2} value={f.tr_id_so_what} onChange={set("tr_id_so_what")} /></Field>
        <Field label="Isi Artikel (ID)"><textarea className={inputCls} rows={4} value={f.tr_id_body} onChange={set("tr_id_body")} /></Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="What Matters Rank (1-5)"><input className={inputCls} type="number" value={f.matters_rank} onChange={set("matters_rank")} /></Field>
        <Field label="Featured?"><select className={inputCls} value={f.featured ? "1" : "0"} onChange={(e) => setF({ ...f, featured: e.target.value === "1" })}><option value="0">No</option><option value="1">Yes</option></select></Field>
      </div>
      <button data-testid="article-submit" className={`mt-6 ${btn}`}>{initial?.id ? "Save Changes" : "Publish Article"}</button>
    </form>
  );
}

function AlphaForm({ initial, onDone, analysts }) {
  const empty = { market: "CRYPTO", asset: "", headline: "", observed: "", interpretation: "", confidence: "MEDIUM", impact: "MEDIUM", source: "", tier: "public", signal_score: "", badges: [], analyst_id: "", exclusive: false };
  const fromDoc = (d) => ({ ...empty, ...d, badges: d.badges || [], signal_score: d.signal_score ?? "" });
  const [f, setF] = useState(initial ? fromDoc(initial) : empty);
  useEffect(() => { setF(initial ? fromDoc(initial) : empty); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const MARKETS = ["CRYPTO", "US EQUITIES", "ON-CHAIN", "SMART MONEY", "MACRO", "DEGEN", "FUND FLOWS", "INSTITUTIONAL", "SEA"];
  const BADGES = ["BREAKING", "DEVELOPING", "CONFIRMED", "ON-CHAIN", "EXCLUSIVE", "OFFICIAL", "UNCONFIRMED"];
  const toggleBadge = (b) => setF({ ...f, badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b] });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { market: f.market, asset: f.asset, headline: f.headline, observed: f.observed, interpretation: f.interpretation, confidence: f.confidence, impact: f.impact, source: f.source, tier: f.tier, badges: f.badges, signal_score: f.signal_score === "" ? null : parseFloat(f.signal_score), analyst_id: f.analyst_id || "", exclusive: !!f.exclusive };
    try { if (initial?.id) await client.put(`/alpha/${initial.id}`, payload); else await client.post("/alpha", payload); onDone(); }
    catch { toast.error("Failed."); }
  };
  return (
    <form onSubmit={submit} className="max-w-2xl" data-testid="alpha-form">
      <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => setF({ ...f, analyst_id: v })} />
      <ExclusiveField value={f.exclusive} onChange={(v) => setF({ ...f, exclusive: v })} testid="alpha-exclusive" />
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Market"><select className={inputCls} value={f.market} onChange={set("market")}>{MARKETS.map((m) => <option key={m}>{m}</option>)}</select></Field>
        <Field label="Asset"><input data-testid="alpha-asset" className={inputCls} value={f.asset} onChange={set("asset")} /></Field>
        <Field label="Signal (0-10)"><input className={inputCls} type="number" step="0.1" value={f.signal_score} onChange={set("signal_score")} /></Field>
      </div>
      <Field label="Headline"><input data-testid="alpha-headline" className={inputCls} value={f.headline} onChange={set("headline")} required /></Field>
      <Field label="Observed Data"><textarea className={inputCls} rows={2} value={f.observed} onChange={set("observed")} /></Field>
      <Field label="OnchainLyfe Interpretation"><textarea className={inputCls} rows={2} value={f.interpretation} onChange={set("interpretation")} /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Confidence"><select className={inputCls} value={f.confidence} onChange={set("confidence")}><option>LOW</option><option>MEDIUM</option><option>HIGH</option></select></Field>
        <Field label="Impact"><input className={inputCls} value={f.impact} onChange={set("impact")} /></Field>
        <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="member">Member</option><option value="alpha">Alpha</option></select></Field>
      </div>
      <Field label="Source"><input className={inputCls} value={f.source} onChange={set("source")} /></Field>
      <label className={labelCls}>Badges</label>
      <div className="flex flex-wrap gap-2">
        {BADGES.map((b) => <button type="button" key={b} onClick={() => toggleBadge(b)} className={`px-3 py-1 rounded-full text-[10px] font-mono-cl uppercase border ${f.badges.includes(b) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{b}</button>)}
      </div>
      <button data-testid="alpha-submit" className={`mt-6 ${btn}`}>{initial?.id ? "Save Signal" : "Publish Signal"}</button>
    </form>
  );
}

function ConvictionSection({ analysts, onChange }) {
  const [view, setView] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const done = () => { setView("list"); setEditing(null); onChange?.(); toast.success("Thesis saved."); };

  if (view === "form") {
    return (
      <div data-testid="conviction-section-form">
        <button data-testid="conviction-back-to-list" onClick={() => { setView("list"); setEditing(null); }}
          className="mb-5 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#ccff00] transition-colors">
          ← All conviction
        </button>
        <ConvictionForm initial={editing} analysts={analysts} onDone={done} />
      </div>
    );
  }
  return <ConvictionManager onNew={() => { setEditing(null); setView("form"); }} onEdit={(doc) => { setEditing(doc); setView("form"); }} onChange={onChange} />;
}

function ConvictionManager({ onNew, onEdit, onChange }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [assetType, setAssetType] = useState("");
  const [tier, setTier] = useState("");
  const [pub, setPub] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [pending, setPending] = useState(null);

  const load = () => client.get("/admin/conviction").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (q && !`${r.asset} ${r.title}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (status && r.status !== status) return false;
    if (assetType && (r.track_type || "crypto") !== assetType) return false;
    if (tier && (r.tier || "member") !== tier) return false;
    if (pub === "published" && r.published === false) return false;
    if (pub === "unpublished" && r.published !== false) return false;
    return true;
  });

  const togglePublish = async (r) => {
    setBusyId(r.id);
    try {
      await client.patch(`/conviction/${r.id}/publish`, { published: r.published === false });
      toast.success(r.published === false ? "Published." : "Unpublished.");
      load(); onChange?.();
    } catch { toast.error("Action failed."); }
    finally { setBusyId(null); }
  };
  const confirmDelete = async () => {
    if (!pending) return;
    setBusyId(pending.id);
    try { await client.delete(`/conviction/${pending.id}`); toast.success("Conviction deleted."); setPending(null); load(); onChange?.(); }
    catch { toast.error("Delete failed."); }
    finally { setBusyId(null); }
  };

  const Sel = ({ value, onChange: oc, opts, testid }) => (
    <select data-testid={testid} value={value} onChange={(e) => oc(e.target.value)} className="rounded-lg bg-[#0d0d0d] border border-white/10 px-3 py-2 text-xs">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "-");
  const th = "px-3 py-2.5 text-left text-[10px] font-mono-cl uppercase tracking-[0.12em] text-zinc-500 whitespace-nowrap";
  const td = "px-3 py-3 text-sm whitespace-nowrap";

  return (
    <div data-testid="conviction-manager">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="conviction-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset / title…" className="rounded-lg bg-[#0d0d0d] border border-white/10 pl-9 pr-3 py-2 text-sm w-56" />
        </div>
        <Sel testid="conviction-filter-status" value={status} onChange={setStatus} opts={[["", "All status"], ...CONVICTION_STATUSES.map((s) => [s, s])]} />
        <Sel testid="conviction-filter-type" value={assetType} onChange={setAssetType} opts={[["", "All assets"], ["crypto", "Crypto"], ["stock", "US Stock"], ["none", "Manual"]]} />
        <Sel testid="conviction-filter-tier" value={tier} onChange={setTier} opts={[["", "All tiers"], ["member", "Member"], ["alpha", "Alpha"]]} />
        <Sel testid="conviction-filter-published" value={pub} onChange={setPub} opts={[["", "All"], ["published", "Published"], ["unpublished", "Unpublished"]]} />
        <button data-testid="conviction-new" onClick={onNew} className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ccff00] text-black text-sm font-semibold hover:brightness-110 transition-[filter]">
          <Plus className="w-4 h-4" /> New Conviction
        </button>
      </div>

      <div className="cl-card overflow-x-auto" style={{ transform: "none" }}>
        <table className="w-full min-w-[1050px] border-collapse">
          <thead>
            <tr className="border-b border-white/10">
              <th className={th}>Asset</th><th className={th}>Thesis Title</th><th className={th}>Status</th>
              <th className={th}>Entry</th><th className={th}>Live</th><th className={th}>Performance</th>
              <th className={th}>Horizon</th><th className={th}>Tier</th><th className={th}>Published</th>
              <th className={th}>Updated</th><th className={`${th} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={11} className="text-zinc-500 p-8 text-center">No conviction entries match.</td></tr>
            ) : filtered.map((r) => {
              const neg = (r.performance_value ?? 0) < 0;
              return (
                <tr key={r.id} data-testid={`conviction-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className={`${td} font-display font-bold`}>{r.asset}</td>
                  <td className={`${td} max-w-[220px] truncate`} title={r.title}>{r.title}</td>
                  <td className={td}><span className={`font-mono-cl text-[10px] tracking-[0.1em] uppercase px-2 py-0.5 rounded-full border ${convictionBadgeClass(r.status)}`}>{r.status}</span></td>
                  <td className={`${td} text-zinc-400`}>{r.entry_price != null ? `$${r.entry_price}` : "-"}</td>
                  <td className={`${td} text-zinc-400`}>{r.live_price != null ? `$${r.live_price}` : "-"}</td>
                  <td className={`${td} font-mono-cl ${r.performance ? (neg ? "text-red-400" : "text-[#ccff00]") : "text-zinc-500"}`}>{r.performance || "-"}</td>
                  <td className={`${td} text-zinc-400`}>{r.time_horizon || "-"}</td>
                  <td className={`${td} text-zinc-400 capitalize`}>{r.tier || "member"}</td>
                  <td className={td}>
                    <span className={`font-mono-cl text-[10px] uppercase ${r.published === false ? "text-zinc-500" : "text-emerald-400"}`}>{r.published === false ? "Draft" : "Live"}</span>
                    <span className="block text-[10px] text-zinc-600">{fmtDate(r.date_published)}</span>
                  </td>
                  <td className={`${td} text-[11px] text-zinc-500`}>{fmtDate(r.updated_at || r.created_at)}</td>
                  <td className={td}>
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`conviction-edit-${r.id}`} title="Edit" onClick={() => onEdit(r)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
                      <a data-testid={`conviction-view-${r.id}`} title="View" href={`/conviction/${r.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Eye className="w-4 h-4" /></a>
                      {r.published === false ? (
                        <button data-testid={`conviction-publish-${r.id}`} title="Publish" disabled={busyId === r.id} onClick={() => togglePublish(r)} className="p-2 rounded-lg text-emerald-400 hover:bg-emerald-500/10"><Send className="w-4 h-4" /></button>
                      ) : (
                        <button data-testid={`conviction-unpublish-${r.id}`} title="Unpublish" disabled={busyId === r.id} onClick={() => togglePublish(r)} className="p-2 rounded-lg text-amber-400 hover:bg-amber-500/10"><Archive className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`conviction-delete-${r.id}`} title="Delete" disabled={busyId === r.id} onClick={() => setPending({ id: r.id, label: `${r.asset} · ${r.title}` })} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pending && (
        <div data-testid="conviction-delete-modal" className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !busyId && setPending(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0c0c] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 grid place-items-center"><Trash2 className="w-5 h-5 text-red-400" /></div>
              <h3 className="font-display text-xl font-bold">Delete Conviction?</h3>
            </div>
            <p className="text-sm text-zinc-400">Permanently delete "{pending.label}"? This cannot be undone. To keep the record but hide it, use Unpublish instead.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button data-testid="conviction-delete-cancel" disabled={busyId} onClick={() => setPending(null)} className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-zinc-200 hover:border-white/40 transition-colors disabled:opacity-50">Cancel</button>
              <button data-testid="conviction-delete-confirm" disabled={busyId} onClick={confirmDelete} className="px-5 py-2.5 rounded-full bg-red-500 text-white font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-50">{busyId ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConvictionForm({ initial, onDone, analysts }) {
  const empty = { asset: "", title: "", status: "HIGH CONVICTION", time_horizon: "6-18 Months", thesis: "", catalysts: "", risks: "", invalidation: "", track_symbol: "", track_type: "crypto", entry_price: "", performance: "", performance_value: "", what_we_learned: "", tier: "member", published: true, analyst_id: "", exclusive: false };
  const fromDoc = (d) => ({ ...empty, ...d, catalysts: (d.catalysts || []).join(", "), risks: (d.risks || []).join(", "), entry_price: d.entry_price ?? "", performance_value: d.performance_value ?? "", published: d.published !== false });
  const [f, setF] = useState(initial ? fromDoc(initial) : empty);
  useEffect(() => { setF(initial ? fromDoc(initial) : empty); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { asset: f.asset, title: f.title, status: f.status, time_horizon: f.time_horizon, thesis: f.thesis,
      catalysts: f.catalysts ? f.catalysts.split(",").map((s) => s.trim()).filter(Boolean) : [],
      risks: f.risks ? f.risks.split(",").map((s) => s.trim()).filter(Boolean) : [],
      invalidation: f.invalidation,
      track_symbol: f.track_symbol.trim().toUpperCase(),
      track_type: f.track_type,
      entry_price: f.entry_price === "" ? null : parseFloat(f.entry_price),
      performance: f.performance,
      performance_value: f.performance_value === "" ? null : parseFloat(f.performance_value),
      what_we_learned: f.what_we_learned, tier: f.tier, published: !!f.published, timeline: initial?.timeline || [], analyst_id: f.analyst_id || "", exclusive: !!f.exclusive };
    try { if (initial?.id) await client.put(`/conviction/${initial.id}`, payload); else await client.post("/conviction", payload); onDone(); }
    catch { toast.error("Failed."); }
  };
  return (
    <form onSubmit={submit} className="max-w-2xl" data-testid="conviction-form">
      <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => setF({ ...f, analyst_id: v })} />
      <ExclusiveField value={f.exclusive} onChange={(v) => setF({ ...f, exclusive: v })} testid="conviction-exclusive" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Asset (display, e.g. HYPE)"><input data-testid="conviction-asset" className={inputCls} value={f.asset} onChange={set("asset")} required /></Field>
        <Field label="Thesis Title"><input data-testid="conviction-title" className={inputCls} value={f.title} onChange={set("title")} required /></Field>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Status"><select data-testid="conviction-status" className={inputCls} value={f.status} onChange={set("status")}>{CONVICTION_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Time Horizon"><input className={inputCls} value={f.time_horizon} onChange={set("time_horizon")} /></Field>
        <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="member">Member</option><option value="alpha">Alpha</option></select></Field>
        <Field label="Visibility"><select data-testid="conviction-published" className={inputCls} value={f.published ? "1" : "0"} onChange={(e) => setF({ ...f, published: e.target.value === "1" })}><option value="1">Published</option><option value="0">Unpublished (draft)</option></select></Field>
      </div>

      <div className="rounded-xl border border-[#ccff00]/20 p-4 mt-6">
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-1">Live Performance Tracking</p>
        <p className="text-xs text-zinc-500 mb-1">Enter the ticker + the price when you publish. Performance updates automatically from the live market price, so users see the running % gain/loss. Works for crypto (OKX) and US stocks (e.g. MSTR, NVDA, AAPL).</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Track Type"><select data-testid="conviction-track-type" className={inputCls} value={f.track_type} onChange={set("track_type")}><option value="crypto">Crypto</option><option value="stock">US Stock</option><option value="none">None (manual)</option></select></Field>
          <Field label="Track Symbol"><input data-testid="conviction-track-symbol" className={inputCls} value={f.track_symbol} onChange={set("track_symbol")} placeholder={f.track_type === "stock" ? "MSTR" : "HYPE"} /></Field>
          <Field label="Entry Price (USD)"><input data-testid="conviction-entry-price" className={inputCls} type="number" step="any" value={f.entry_price} onChange={set("entry_price")} placeholder="180.00" /></Field>
        </div>
        <p className="text-[11px] text-zinc-600 mt-1">Leave both blank to enter performance manually below (e.g. for stocks or closed theses).</p>
      </div>

      <Field label="Thesis (why you have conviction)"><textarea className={inputCls} rows={3} value={f.thesis} onChange={set("thesis")} /></Field>
      <Field label="Catalysts: what could drive it (comma separated)"><input className={inputCls} value={f.catalysts} onChange={set("catalysts")} /></Field>
      <Field label="Risks: what could go wrong (comma separated)"><input className={inputCls} value={f.risks} onChange={set("risks")} /></Field>
      <Field label="Invalidation: what would make you abandon the thesis"><textarea className={inputCls} rows={2} value={f.invalidation} onChange={set("invalidation")} placeholder="e.g. Two consecutive quarters of declining revenue" /></Field>

      <div className="rounded-xl border border-white/10 p-4 mt-6">
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-1">Manual Performance (fallback / closed theses)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Performance label (e.g. +38%)"><input className={inputCls} value={f.performance} onChange={set("performance")} /></Field>
          <Field label="Performance value (number, e.g. 38 or -19)"><input className={inputCls} type="number" step="0.1" value={f.performance_value} onChange={set("performance_value")} /></Field>
        </div>
      </div>
      {f.status === "INVALIDATED" && <Field label="What We Learned"><textarea className={inputCls} rows={2} value={f.what_we_learned} onChange={set("what_we_learned")} /></Field>}
      <button data-testid="conviction-submit" className={`mt-6 ${btn}`}>{initial?.id ? "Save Thesis" : "Publish Thesis"}</button>
    </form>
  );
}

function CalendarManager({ onChange }) {
  const [events, setEvents] = useState([]);
  const empty = { title: "", category: "FOMC", date: "", time: "", description: "", importance: "MEDIUM", region: "global", tier: "public" };
  const [f, setF] = useState(empty);
  const load = () => client.get("/calendar").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await client.post("/calendar", f); setF(empty); load(); onChange(); toast.success("Event added."); } catch { toast.error("Failed."); } };
  const del = async (id) => { if (!window.confirm("Delete event?")) return; await client.delete(`/calendar/${id}`); load(); onChange(); toast.success("Deleted."); };
  const CATEGORIES = ["FOMC", "CPI", "NFP", "Earnings", "Token Unlock", "Protocol Upgrade", "ETF Decision", "Regulatory", "Conference", "Launch"];
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} data-testid="calendar-form">
        <Field label="Title"><input data-testid="calendar-title" className={inputCls} value={f.title} onChange={set("title")} required /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category"><select className={inputCls} value={f.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Importance"><select className={inputCls} value={f.importance} onChange={set("importance")}><option>HIGH</option><option>MEDIUM</option><option>LOW</option></select></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Date"><input data-testid="calendar-date" className={inputCls} type="date" value={f.date} onChange={set("date")} required /></Field>
          <Field label="Time"><input className={inputCls} value={f.time} onChange={set("time")} placeholder="20:30 SGT" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Region"><select className={inputCls} value={f.region} onChange={set("region")}><option value="global">Global</option><option value="sea">SEA</option><option value="indonesia">Indonesia</option></select></Field>
          <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="alpha">Alpha</option></select></Field>
        </div>
        <Field label="Description"><textarea className={inputCls} rows={3} value={f.description} onChange={set("description")} /></Field>
        <button data-testid="calendar-submit" className={`mt-6 ${btn}`}>Add Event</button>
      </form>
      <div className="cl-card overflow-hidden self-start" style={{ transform: "none" }}>
        {events.map((ev) => (
          <div key={ev.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5">
            <div className="min-w-0"><p className="text-sm font-medium truncate">{ev.title}</p><p className="font-mono-cl text-[10px] uppercase text-zinc-500">{ev.date} · {ev.category} · {ev.tier}</p></div>
            <button onClick={() => del(ev.id)} className="shrink-0 p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Manage({ articles, alpha, conviction, onEdit, onChange }) {
  const [sub, setSub] = useState("articles");
  const [pending, setPending] = useState(null); // { kind, id, label }
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const confirmDelete = async () => {
    if (!pending) return;
    setBusy(true); setErrMsg("");
    try {
      await client.delete(`/${pending.kind}/${pending.id}`);
      setBusy(false); setPending(null);
      toast.success("Article deleted successfully.");
      onChange(); // parent refetches from DB — no optimistic hide
    } catch (e) {
      setBusy(false);
      const msg = e?.response?.data?.detail || "Delete failed. The item was not removed.";
      setErrMsg(typeof msg === "string" ? msg : "Delete failed.");
      toast.error("Could not delete. Please try again.");
    }
  };

  const rows = sub === "articles" ? articles : sub === "alpha" ? alpha : conviction;
  const label = (x) => sub === "articles" ? x.title : sub === "alpha" ? x.headline : `${x.asset} - ${x.title}`;
  const meta = (x) => sub === "articles" ? `${x.type} · ${x.category} · ${x.region} · ${x.tier}` : sub === "alpha" ? `${x.market} · ${x.tier}` : `${x.status} · ${x.tier}`;
  const kindPath = sub === "articles" ? "articles" : sub;
  const editKind = sub === "articles" ? "article" : sub === "alpha" ? "alpha" : "conviction";
  const kindWord = sub === "articles" ? "Article" : sub === "alpha" ? "Signal" : "Thesis";
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[["articles", "Articles"], ["alpha", "Alpha"], ["conviction", "Conviction"]].map(([k, l]) => (
          <button key={k} data-testid={`manage-sub-${k}`} onClick={() => setSub(k)} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${sub === k ? "bg-[#ccff00] text-black" : "border border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
        {rows.length === 0 ? <p className="text-zinc-500 p-8">Nothing here.</p> : rows.map((x) => (
          <div key={x.id} data-testid={`manage-row-${x.id}`} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5">
            <div className="min-w-0"><p className="text-sm font-medium truncate">{label(x)}</p><p className="font-mono-cl text-[10px] tracking-[0.12em] uppercase text-zinc-500">{meta(x)}</p></div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`edit-${x.id}`} onClick={() => onEdit(editKind, x)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-${x.id}`} onClick={() => { setErrMsg(""); setPending({ kind: kindPath, id: x.id, label: label(x) }); }} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {pending && (
        <div data-testid="delete-confirm-modal" className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !busy && setPending(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0c0c] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 grid place-items-center"><Trash2 className="w-5 h-5 text-red-400" /></div>
              <h3 className="font-display text-xl font-bold">Delete {kindWord}?</h3>
            </div>
            <p className="text-sm text-zinc-400">Are you sure you want to <strong className="text-zinc-200">permanently delete</strong> "{pending.label}"? This cannot be undone.</p>
            {errMsg && <p data-testid="delete-error" className="text-sm text-red-400 mt-3 rounded-lg border border-red-500/30 bg-red-500/[0.06] p-3">{errMsg}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button data-testid="delete-cancel" disabled={busy} onClick={() => setPending(null)} className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-zinc-200 hover:border-white/40 transition-colors disabled:opacity-50">Cancel</button>
              <button data-testid="delete-confirm" disabled={busy} onClick={confirmDelete} className="px-5 py-2.5 rounded-full bg-red-500 text-white font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-50">{busy ? "Deleting…" : `Delete ${kindWord}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GrantPanel({ target, onDone }) {
  const [cats, setCats] = useState(target.alpha_categories || []);
  const [type, setType] = useState(target.access_type || "paid");
  const [months, setMonths] = useState(3);
  const toggle = (c) => setCats(cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c]);
  const grant = async () => {
    try { await client.post(`/admin/users/${target.id}/grant`, { categories: cats, access_type: type, duration_months: Number(months) }); toast.success("Access granted."); onDone(); }
    catch { toast.error("Failed."); }
  };
  return (
    <div className="mt-3 rounded-xl border border-[#ccff00]/20 p-4" data-testid={`grant-panel-${target.id}`}>
      <p className="font-mono-cl text-[10px] tracking-[0.16em] uppercase text-[#ccff00] mb-2">Category Access Checklist</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {CATS.map(([k, l]) => (
          <button key={k} onClick={() => toggle(k)} data-testid={`grant-cat-${k}`} className={`px-3 py-1 rounded-full text-[11px] border ${cats.includes(k) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <div><label className="block text-[10px] font-mono-cl uppercase text-zinc-500 mb-1">Type</label>
          <select className={inputCls + " w-auto"} value={type} onChange={(e) => setType(e.target.value)}><option value="paid">Paid</option><option value="kol">KOL Access</option></select></div>
        <div><label className="block text-[10px] font-mono-cl uppercase text-zinc-500 mb-1">Duration</label>
          <select className={inputCls + " w-auto"} value={months} onChange={(e) => setMonths(e.target.value)}>{[1, 3, 6, 12].map((m) => <option key={m} value={m}>{m} mo</option>)}</select></div>
        <button onClick={grant} data-testid={`grant-submit-${target.id}`} className="px-5 py-2.5 rounded-full bg-[#ccff00] text-black text-sm font-semibold">Grant Access</button>
        <button onClick={() => setCats(CAT_KEYS)} className="px-3 py-2.5 text-xs text-zinc-400 hover:text-white">Select all</button>
      </div>
    </div>
  );
}

function AnalystSubPanel({ userId, analysts, onDone }) {
  const [aid, setAid] = useState(analysts[0]?.id || "");
  const [days, setDays] = useState(30);
  const grant = async () => {
    if (!aid) return;
    try { await client.post(`/admin/users/${userId}/subscribe/${aid}`, { days: Number(days) }); toast.success("Subscription granted."); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="mt-3 rounded-xl border border-white/10 p-4" data-testid={`sub-panel-${userId}`}>
      <p className="font-mono-cl text-[10px] tracking-[0.16em] uppercase text-zinc-400 mb-2">Grant Analyst Subscription (comp / KOL)</p>
      <div className="flex flex-wrap gap-3 items-end">
        <div><label className="block text-[10px] font-mono-cl uppercase text-zinc-500 mb-1">Analyst</label>
          <select className={inputCls + " w-auto"} value={aid} onChange={(e) => setAid(e.target.value)}>{analysts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        <div><label className="block text-[10px] font-mono-cl uppercase text-zinc-500 mb-1">Duration</label>
          <select className={inputCls + " w-auto"} value={days} onChange={(e) => setDays(e.target.value)}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</select></div>
        <button onClick={grant} data-testid={`sub-grant-${userId}`} className="px-5 py-2.5 rounded-full bg-[#ccff00] text-black text-sm font-semibold">Grant Subscription</button>
      </div>
    </div>
  );
}

function UsersManager({ analysts = [] }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [subOpen, setSubOpen] = useState(null);
  const [showStaff, setShowStaff] = useState(false);
  const load = () => client.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const revoke = async (id) => { await client.post(`/admin/users/${id}/revoke`); toast.success("Access revoked."); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-400">{users.length} users</p>
        <button data-testid="toggle-staff-form" onClick={() => setShowStaff((v) => !v)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:border-[#ccff00]/40"><UserPlus className="w-4 h-4" /> Create Admin / Publisher</button>
      </div>
      {showStaff && <StaffForm onDone={() => { setShowStaff(false); load(); }} />}
      <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="px-4 py-3 border-b border-white/5">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{u.name} <span className="text-zinc-500 font-normal">· {u.email}</span></p>
                <p className="font-mono-cl text-[10px] tracking-[0.1em] uppercase text-zinc-500">
                  {u.role} · {u.phone || "no phone"} · {u.alpha_categories.length ? `${u.access_type || "access"} (${u.alpha_categories.length} cats, exp ${u.access_expires_at ? u.access_expires_at.slice(0, 10) : "-"})` : "reader"}{u.referred_by ? ` · ref ${u.referred_by}` : ""}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`manage-access-${u.id}`} onClick={() => { setOpen(open === u.id ? null : u.id); setSubOpen(null); }} className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:border-[#ccff00]/40">Access</button>
                {analysts.length > 0 && <button data-testid={`manage-sub-${u.id}`} onClick={() => { setSubOpen(subOpen === u.id ? null : u.id); setOpen(null); }} className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:border-[#ccff00]/40">Sub</button>}
                {u.alpha_categories.length > 0 && u.role !== "admin" && <button onClick={() => revoke(u.id)} className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10">Revoke</button>}
              </div>
            </div>
            {open === u.id && <GrantPanel target={u} onDone={() => { setOpen(null); load(); }} />}
            {subOpen === u.id && <AnalystSubPanel userId={u.id} analysts={analysts} onDone={() => setSubOpen(null)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "publisher" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await client.post("/admin/staff", f); toast.success("Staff account created."); onDone(); } catch (ex) { toast.error(ex.response?.data?.detail || "Failed."); } };
  return (
    <form onSubmit={submit} data-testid="staff-form" className="cl-card p-5 mb-4" style={{ transform: "none" }}>
      <div className="grid sm:grid-cols-4 gap-3">
        <input className={inputCls} placeholder="Name" value={f.name} onChange={set("name")} required data-testid="staff-name" />
        <input className={inputCls} placeholder="Email" type="email" value={f.email} onChange={set("email")} required data-testid="staff-email" />
        <input className={inputCls} placeholder="Password" type="password" value={f.password} onChange={set("password")} required data-testid="staff-password" />
        <select className={inputCls} value={f.role} onChange={set("role")}><option value="publisher">Publisher</option><option value="admin">Admin</option></select>
      </div>
      <button data-testid="staff-submit" className={`mt-4 ${btn}`}>Create Account</button>
    </form>
  );
}

function ReferralsManager() {
  const [refs, setRefs] = useState([]);
  const [label, setLabel] = useState("");
  const origin = window.location.origin;
  const load = () => client.get("/admin/referrals").then((r) => setRefs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await client.post("/admin/referrals", { label }); setLabel(""); load(); toast.success("Referral code created."); } catch { toast.error("Failed."); } };
  const del = async (id) => { if (!window.confirm("Delete referral?")) return; await client.delete(`/admin/referrals/${id}`); load(); toast.success("Deleted."); };
  const linkFor = (code) => `${origin}/register?ref=${code}`;
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); } };
  return (
    <div>
      <form onSubmit={create} data-testid="referral-form" className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[220px]">
          <label className={labelCls}>Label (e.g. KOL name / campaign)</label>
          <input data-testid="referral-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="KOL · @someone" />
        </div>
        <button data-testid="referral-create" className={btn}>Generate Code + Link</button>
      </form>
      <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
        <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.02] font-mono-cl text-[10px] tracking-[0.14em] uppercase text-zinc-600">
          <span className="col-span-3">Code / Label</span><span className="col-span-5">Link</span>
          <span className="col-span-1 text-center">Signups</span><span className="col-span-1 text-center">Alpha</span><span className="col-span-2 text-right">Actions</span>
        </div>
        {refs.length === 0 ? <p className="text-zinc-500 p-8">No referral codes yet.</p> : refs.map((r) => (
          <div key={r.id} data-testid={`referral-row-${r.code}`} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center px-4 py-3 border-b border-white/5">
            <div className="sm:col-span-3">
              <p className="font-mono-cl font-bold text-[#ccff00]">{r.code}</p>
              {r.label && <p className="text-xs text-zinc-500 truncate">{r.label}</p>}
            </div>
            <div className="sm:col-span-5 min-w-0">
              <button onClick={() => copy(linkFor(r.code))} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#ccff00] truncate max-w-full">
                <Copy className="w-3 h-3 shrink-0" /> <span className="truncate">{linkFor(r.code)}</span>
              </button>
            </div>
            <div className="sm:col-span-1 text-center"><span className="font-mono-cl text-lg font-bold">{r.signups}</span></div>
            <div className="sm:col-span-1 text-center"><span className="font-mono-cl text-lg font-bold text-[#ccff00]">{r.alpha}</span></div>
            <div className="sm:col-span-2 flex justify-end gap-1">
              <button data-testid={`referral-copy-${r.code}`} onClick={() => copy(r.code)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5" title="Copy code"><Copy className="w-4 h-4" /></button>
              <button data-testid={`referral-delete-${r.id}`} onClick={() => del(r.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestsManager() {
  const [reqs, setReqs] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => client.get("/admin/alpha-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const reject = async (id) => { await client.post(`/admin/users/${id}/reject`); toast.success("Request rejected."); load(); };
  return (
    <div>
      <p className="text-sm text-zinc-400 mb-4">{reqs.length} pending Join Alpha requests</p>
      {reqs.length === 0 ? <p className="text-zinc-500 py-12 text-center">No pending requests.</p> : (
        <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
          {reqs.map((u) => (
            <div key={u.id} data-testid={`request-row-${u.id}`} className="px-4 py-3 border-b border-white/5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.name} <span className="text-zinc-500 font-normal">· {u.email} · {u.phone || "no phone"}</span></p>
                  <p className="font-mono-cl text-[10px] uppercase text-[#ccff00]">Plan: {u.alpha_request?.plan || "alpha"} · waitlisted</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`approve-${u.id}`} onClick={() => setOpen(open === u.id ? null : u.id)} className="px-3 py-1.5 rounded-full bg-[#ccff00] text-black text-xs font-semibold">Approve</button>
                  <button data-testid={`reject-${u.id}`} onClick={() => reject(u.id)} className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10">Reject</button>
                </div>
              </div>
              {open === u.id && <GrantPanel target={u} onDone={() => { setOpen(null); load(); }} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const ANALYST_TYPES = ["Degen", "Memecoin", "US Stocks", "Macro", "On-Chain", "Perps", "DeFi", "NFT"];

function ModulesManager({ isAnalyst, analysts = [] }) {
  const [mods, setMods] = useState([]);
  const [myId, setMyId] = useState("");
  const empty = { title: "", module_type: "writing", description: "", content: "", links: "", image: "", video_url: "", tier: "public", analyst_id: "" };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = async () => {
    if (isAnalyst) {
      try {
        const me = await client.get("/me/analyst");
        setMyId(me.data.id);
        const r = await client.get(`/modules?analyst_id=${me.data.id}`);
        setMods(r.data);
      } catch { setMods([]); }
    } else {
      const r = await client.get("/modules");
      setMods(r.data);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: f.title, module_type: f.module_type, description: f.description, content: f.content,
      links: f.links ? f.links.split(",").map((s) => s.trim()).filter(Boolean) : [],
      image: f.image, video_url: f.video_url, tier: f.tier, analyst_id: f.analyst_id || "",
    };
    try {
      if (editId) await client.put(`/modules/${editId}`, payload);
      else await client.post("/modules", payload);
      setF(empty); setEditId(null); load(); toast.success("Module saved.");
    } catch (ex) { toast.error(ex.response?.data?.detail || "Failed. Attribute to an analyst."); }
  };
  const editMod = (m) => { setEditId(m.id); setF({ ...empty, ...m, links: (m.links || []).join(", "), analyst_id: m.analyst_id || "" }); };
  const del = async (id) => { if (!window.confirm("Delete module?")) return; await client.delete(`/modules/${id}`); load(); toast.success("Deleted."); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={submit} data-testid="module-form">
        {!isAnalyst && <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => setF({ ...f, analyst_id: v })} />}
        <Field label="Module Title"><input data-testid="module-title" className={inputCls} value={f.title} onChange={set("title")} required /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type"><select data-testid="module-type" className={inputCls} value={f.module_type} onChange={set("module_type")}><option value="writing">Written</option><option value="video">Video</option></select></Field>
          <Field label="Access"><select className={inputCls} value={f.tier} onChange={set("tier")}><option value="public">Public</option><option value="subscribers">Subscribers only</option></select></Field>
        </div>
        <Field label="Short Description"><input className={inputCls} value={f.description} onChange={set("description")} /></Field>
        {f.module_type === "video" && <Field label="Video URL (YouTube / Vimeo / uploaded link)"><input data-testid="module-video-url" className={inputCls} value={f.video_url} onChange={set("video_url")} placeholder="https://youtube.com/watch?v=…" /></Field>}
        <Field label="Cover Image"><FileUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} label="Upload cover image" testid="module-image" /></Field>
        <Field label="Content (body text)"><textarea className={inputCls} rows={6} value={f.content} onChange={set("content")} /></Field>
        <Field label="Reference Links (comma separated)"><input className={inputCls} value={f.links} onChange={set("links")} placeholder="https://…, https://…" /></Field>
        <div className="flex gap-2 mt-6">
          <button data-testid="module-submit" className={btn}>{editId ? "Save Module" : "Publish Module"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setF(empty); }} className="px-5 py-3 rounded-full border border-white/10 text-sm">Cancel</button>}
        </div>
      </form>
      <div className="cl-card overflow-hidden self-start" style={{ transform: "none" }}>
        {mods.length === 0 ? <p className="text-zinc-500 p-8">No modules yet.</p> : mods.map((m) => (
          <div key={m.id} data-testid={`module-row-${m.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5">
            <div className="min-w-0"><p className="text-sm font-medium truncate">{m.title}</p><p className="font-mono-cl text-[10px] uppercase text-zinc-500">{m.module_type} · {m.tier}{m.analyst_name ? ` · ${m.analyst_name}` : ""}</p></div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => editMod(m)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(m.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalystProfileEditor({ initial, onSave, testid = "analyst-profile" }) {
  const empty = { name: "", tagline: "", bio: "", types: [], big_picture: "", profile_image: "", intro_video_url: "", instagram: "" };
  const fromDoc = (d) => ({ ...empty, ...d, types: d.types || [], instagram: d.socials?.instagram || "" });
  const [f, setF] = useState(initial ? fromDoc(initial) : empty);
  useEffect(() => { setF(initial ? fromDoc(initial) : empty); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleType = (t) => setF({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] });
  const submit = (e) => {
    e.preventDefault();
    onSave({ name: f.name, tagline: f.tagline, bio: f.bio, types: f.types, big_picture: f.big_picture, profile_image: f.profile_image, intro_video_url: f.intro_video_url, socials: { instagram: f.instagram } });
  };
  return (
    <form onSubmit={submit} data-testid={`${testid}-form`} className="max-w-2xl">
      <Field label="Display Name"><input data-testid={`${testid}-name`} className={inputCls} value={f.name} onChange={set("name")} /></Field>
      <Field label="Tagline (one line)"><input className={inputCls} value={f.tagline} onChange={set("tagline")} /></Field>
      <label className={labelCls}>Type / Focus</label>
      <div className="flex flex-wrap gap-2">
        {ANALYST_TYPES.map((t) => <button type="button" key={t} onClick={() => toggleType(t)} className={`px-3 py-1 rounded-full text-[10px] font-mono-cl uppercase border ${f.types.includes(t) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{t}</button>)}
      </div>
      <div className="mt-4 rounded-xl border border-[#ccff00]/25 bg-[#ccff00]/[0.04] p-4 space-y-2">
        <p className="font-mono-cl text-[10px] tracking-[0.16em] uppercase text-[#ccff00]">Photo & Header Guideline</p>
        <p className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-white">Profile Photo</strong>: your main portrait, shown on the <strong>homepage spotlight</strong> and on your <strong>analyst card</strong> in the directory. It renders in a <strong>portrait 3:4 frame</strong>, so upload a portrait image, <strong>recommended 1200×1600px</strong>. Keep your face/upper body in the top-center. The live preview below shows the exact framing.
        </p>
        <p className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-white">Header / Cover</strong>: a wide banner shown <strong>only at the top of your analyst profile page</strong>. It renders in a <strong>landscape 16:9 frame</strong>, so upload a wide image, <strong>recommended 1600×900px</strong> (a trading desk, chart, or branded backdrop works well).
        </p>
        <p className="text-[11px] text-zinc-500">Follow these ratios and the live previews confirm nothing important is cropped.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 mt-4 items-start">
        <Field label="Profile Photo (portrait 3:4, 1200×1600)"><FileUpload value={f.profile_image} onChange={(v) => setF({ ...f, profile_image: v })} label="Upload profile photo (3:4)" testid={`${testid}-photo`} aspect="3/4" previewClass="w-32" /></Field>
        <Field label="Header / Cover (landscape 16:9, 1600×900)"><FileUpload value={f.big_picture} onChange={(v) => setF({ ...f, big_picture: v })} label="Upload header (16:9)" testid={`${testid}-banner`} aspect="16/9" previewClass="w-full max-w-xs" objectPosition="center" /></Field>
      </div>
      <Field label="Introduction Video (YouTube / Vimeo link)"><input data-testid={`${testid}-video`} className={inputCls} value={f.intro_video_url} onChange={set("intro_video_url")} placeholder="https://youtube.com/watch?v=…" /></Field>
      <Field label="Biography"><textarea className={inputCls} rows={5} value={f.bio} onChange={set("bio")} /></Field>
      <Field label="Instagram URL"><input className={inputCls} value={f.instagram} onChange={set("instagram")} /></Field>
      <button data-testid={`${testid}-submit`} className={`mt-6 ${btn}`}>Save Profile</button>
    </form>
  );
}

function MyProfile() {
  const [prof, setProf] = useState(null);
  const load = () => client.get("/me/analyst").then((r) => setProf(r.data)).catch(() => setProf(null));
  useEffect(() => { load(); }, []);
  const save = async (payload) => {
    try { await client.put("/me/analyst", payload); toast.success("Profile updated."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  if (!prof) return <p className="text-zinc-500">Loading profile…</p>;
  const saveSettings = async (payload) => {
    try { await client.put("/me/analyst/settings", payload); toast.success("Subscription settings saved."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <div>
      <div className="rounded-2xl border border-[#ccff00]/25 bg-[#ccff00]/[0.03] p-4 mb-6">
        <p className="text-sm text-zinc-300">This is your public analyst page. Keep your intro video, tags and biography sharp. This is how subscribers judge whether to follow you.</p>
      </div>
      <SubscriptionSettings initial={prof} onSave={saveSettings} testid="my" />
      <AnalystProfileEditor initial={prof} onSave={save} testid="my-profile" />
    </div>
  );
}

function SubscriptionSettings({ initial, onSave, testid = "set" }) {
  const [paid, setPaid] = useState(!!initial.paid_enabled);
  const [wl, setWl] = useState(initial.whitelist_enabled !== false);
  const [price, setPrice] = useState(initial.subscription_price || 0);
  const [days, setDays] = useState(initial.subscription_days || 30);
  useEffect(() => { setPaid(!!initial.paid_enabled); setWl(initial.whitelist_enabled !== false); setPrice(initial.subscription_price || 0); setDays(initial.subscription_days || 30); }, [initial]);
  const Toggle = ({ on, onClick, label, sub, tid }) => (
    <button type="button" onClick={onClick} data-testid={tid} className={`flex-1 text-left rounded-xl border p-4 transition-colors ${on ? "border-[#ccff00]/50 bg-[#ccff00]/[0.06]" : "border-white/10"}`}>
      <div className="flex items-center justify-between"><span className="font-semibold text-sm">{label}</span>
        <span className={`w-10 h-6 rounded-full relative transition-colors ${on ? "bg-[#ccff00]" : "bg-zinc-700"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all ${on ? "left-[18px]" : "left-0.5"}`} /></span></div>
      <p className="text-xs text-zinc-400 mt-1">{sub}</p>
    </button>
  );
  return (
    <div className="cl-card p-5 mb-6" style={{ transform: "none" }} data-testid={`${testid}-sub-settings`}>
      <p className="font-mono-cl text-[11px] tracking-[0.16em] uppercase text-[#ccff00] mb-3">Subscription Settings</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Toggle on={wl} onClick={() => setWl(!wl)} label="Whitelist mode" sub="Users request access; you approve manually. Premium & exclusive." tid={`${testid}-toggle-wl`} />
        <Toggle on={paid} onClick={() => setPaid(!paid)} label="Paid subscription (QRIS)" sub="Users pay to subscribe instantly via QRIS." tid={`${testid}-toggle-paid`} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Field label="Price (IDR)"><input type="number" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`${testid}-price`} /></Field>
        <Field label="Duration (days)"><select className={inputCls} value={days} onChange={(e) => setDays(e.target.value)}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</select></Field>
      </div>
      <button type="button" data-testid={`${testid}-save-settings`} onClick={() => onSave({ paid_enabled: paid, whitelist_enabled: wl, subscription_price: parseInt(price || 0), subscription_days: parseInt(days) })} className={`mt-4 ${btn}`}>Save Subscription Settings</button>
    </div>
  );
}

function AnalystsManager() {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const load = () => client.get("/admin/analysts").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setField = async (id, patch) => { try { await client.put(`/admin/analysts/${id}`, patch); load(); toast.success("Updated."); } catch { toast.error("Failed."); } };
  const saveProfile = async (id, payload) => { try { await client.put(`/admin/analysts/${id}/profile`, payload); toast.success("Profile saved."); setEditProfile(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-400">{list.length} analysts</p>
        <button data-testid="toggle-analyst-create" onClick={() => setShowCreate((v) => !v)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:border-[#ccff00]/40"><UserPlus className="w-4 h-4" /> Create Analyst Account</button>
      </div>
      {showCreate && <CreateAnalystForm onDone={() => { setShowCreate(false); load(); }} />}
      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id} data-testid={`analyst-admin-${a.id}`} className="cl-card p-4" style={{ transform: "none" }}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <img src={a.profile_image || "https://via.placeholder.com/48"} alt="" className="w-10 h-10 rounded-lg object-cover border border-white/10" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{a.name} <span className="text-zinc-500 font-normal">· {a.email}</span></p>
                  <p className="font-mono-cl text-[10px] uppercase text-zinc-500">{(a.types || []).join(" · ") || "no tags"} · {a.subscribers} subs · {a.active ? "active" : "hidden"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1"><span className="font-mono-cl text-[10px] uppercase text-zinc-500">Rp</span>
                  <input type="number" defaultValue={a.subscription_price} onBlur={(e) => setField(a.id, { subscription_price: parseInt(e.target.value || "0") })} className={inputCls + " w-28 py-1.5"} data-testid={`analyst-price-${a.id}`} /></div>
                <select defaultValue={a.subscription_days} onChange={(e) => setField(a.id, { subscription_days: parseInt(e.target.value) })} className={inputCls + " w-auto py-1.5"}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d}d</option>)}</select>
                <button data-testid={`analyst-paid-${a.id}`} onClick={() => setField(a.id, { paid_enabled: !a.paid_enabled })} className={`px-3 py-1.5 rounded-full text-xs border ${a.paid_enabled ? "border-[#ccff00]/40 text-[#ccff00]" : "border-white/10 text-zinc-400"}`}>Paid {a.paid_enabled ? "On" : "Off"}</button>
                <button data-testid={`analyst-wl-${a.id}`} onClick={() => setField(a.id, { whitelist_enabled: !a.whitelist_enabled })} className={`px-3 py-1.5 rounded-full text-xs border ${a.whitelist_enabled ? "border-[#ccff00]/40 text-[#ccff00]" : "border-white/10 text-zinc-400"}`}>WL {a.whitelist_enabled ? "On" : "Off"}</button>
                <button data-testid={`analyst-active-${a.id}`} onClick={() => setField(a.id, { active: !a.active })} className={`px-3 py-1.5 rounded-full text-xs border ${a.active ? "border-[#ccff00]/40 text-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{a.active ? "Active" : "Hidden"}</button>
                <button data-testid={`analyst-edit-${a.id}`} onClick={() => setEditProfile(editProfile === a.id ? null : a.id)} className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:border-[#ccff00]/40">Edit Profile</button>
                <a href={`/analyst/${a.slug}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:border-[#ccff00]/40">View</a>
              </div>
            </div>
            {editProfile === a.id && <div className="mt-4 border-t border-white/10 pt-4"><AnalystProfileEditor initial={a} onSave={(p) => saveProfile(a.id, p)} testid={`admin-analyst-${a.id}`} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateAnalystForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", tagline: "", types: [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleType = (t) => setF({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] });
  const submit = async (e) => { e.preventDefault(); try { await client.post("/admin/analysts", f); toast.success("Analyst account created."); onDone(); } catch (ex) { toast.error(ex.response?.data?.detail || "Failed."); } };
  return (
    <form onSubmit={submit} data-testid="create-analyst-form" className="cl-card p-5 mb-4" style={{ transform: "none" }}>
      <div className="grid sm:grid-cols-3 gap-3">
        <input className={inputCls} placeholder="Name" value={f.name} onChange={set("name")} required data-testid="analyst-name" />
        <input className={inputCls} placeholder="Email" type="email" value={f.email} onChange={set("email")} required data-testid="analyst-email" />
        <input className={inputCls} placeholder="Password" type="password" value={f.password} onChange={set("password")} required data-testid="analyst-password" />
      </div>
      <input className={inputCls + " mt-3"} placeholder="Tagline" value={f.tagline} onChange={set("tagline")} />
      <div className="flex flex-wrap gap-2 mt-3">
        {ANALYST_TYPES.map((t) => <button type="button" key={t} onClick={() => toggleType(t)} className={`px-3 py-1 rounded-full text-[10px] font-mono-cl uppercase border ${f.types.includes(t) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-400"}`}>{t}</button>)}
      </div>
      <button data-testid="analyst-create-submit" className={`mt-4 ${btn}`}>Create Account</button>
    </form>
  );
}

function WhitelistManager() {
  const [reqs, setReqs] = useState([]);
  const load = () => client.get("/whitelist/requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (id, approve) => {
    try { await client.post(`/whitelist/${id}/${approve ? "approve" : "reject"}`); toast.success(approve ? "Approved. Access granted." : "Rejected."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <div>
      <p className="text-sm text-zinc-400 mb-4">{reqs.length} pending whitelist request{reqs.length === 1 ? "" : "s"}</p>
      <div className="cl-card overflow-hidden" style={{ transform: "none" }}>
        {reqs.length === 0 ? <p className="text-zinc-500 p-8">No pending requests.</p> : reqs.map((r) => (
          <div key={r.id} data-testid={`wl-req-${r.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{r.user_name} <span className="text-zinc-500 font-normal">· {r.user_email}</span></p>
              <p className="font-mono-cl text-[10px] uppercase text-zinc-500">wants access to {r.analyst_name}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`wl-approve-${r.id}`} onClick={() => decide(r.id, true)} className="px-3 py-1.5 rounded-full bg-[#ccff00] text-black text-xs font-semibold">Approve</button>
              <button data-testid={`wl-reject-${r.id}`} onClick={() => decide(r.id, false)} className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10">Reject</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CodesManager({ isAnalyst, analysts = [] }) {
  const [codes, setCodes] = useState([]);
  const [f, setF] = useState({ analyst_id: "", days: 30, max_uses: 1 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const load = () => client.get("/analyst/codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const gen = async (e) => {
    e.preventDefault();
    try { const { data } = await client.post("/analyst/codes", { analyst_id: f.analyst_id, days: parseInt(f.days), max_uses: parseInt(f.max_uses) }); toast.success(`Code ${data.code} created.`); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Failed. Select an analyst."); }
  };
  const del = async (id) => { await client.delete(`/analyst/codes/${id}`); load(); toast.success("Code deleted."); };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Code copied."); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={gen} data-testid="code-form">
        <p className="text-sm text-zinc-400 mb-4">Generate an access code. Anyone who redeems it gets auto-approved access. Great for KOLs, giveaways and partners.</p>
        {!isAnalyst && <AnalystPicker analysts={analysts} value={f.analyst_id} onChange={(v) => setF({ ...f, analyst_id: v })} />}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Access duration"><select className={inputCls} value={f.days} onChange={set("days")}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}</select></Field>
          <Field label="Max uses"><input type="number" min="1" className={inputCls} value={f.max_uses} onChange={set("max_uses")} data-testid="code-max-uses" /></Field>
        </div>
        <button data-testid="code-generate" className={`mt-2 ${btn}`}>Generate Code</button>
      </form>
      <div className="cl-card overflow-hidden self-start" style={{ transform: "none" }}>
        {codes.length === 0 ? <p className="text-zinc-500 p-8">No codes yet.</p> : codes.map((c) => (
          <div key={c.id} data-testid={`code-row-${c.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/5">
            <div className="min-w-0">
              <p className="font-mono-cl text-sm font-bold text-[#ccff00]">{c.code}</p>
              <p className="font-mono-cl text-[10px] uppercase text-zinc-500">{c.analyst_name} · {c.days}d · {c.uses}/{c.max_uses} used</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => copy(c.code)} className="p-2 rounded-lg text-zinc-300 hover:text-[#ccff00] hover:bg-white/5"><Copy className="w-4 h-4" /></button>
              <button onClick={() => del(c.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

